import concat from 'lodash/concat';
import { ReactElement } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { useApiMutation, useApiRequest } from '~/hooks/api';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import Form from '~/ui/pages/ActTeam/reusable/Form';

import { CLINIC_MANAGEMENT, VIEW_CLINIC } from '~/ui/constants/paths';
import { IInitialValues } from '../types';

type IParams = {
  clinicId: string;
};

const breadcrumbItem = [
  {
    to: CLINIC_MANAGEMENT,
    title: 'Clinic Management',
  },
];

const CreateActTeam = (): ReactElement => {
  const { clinicId } = useParams<IParams>();
  const navigate = useNavigate();
  const location = useLocation();

  const clinic = useStoreState(state => state.clinic.current);

  const onGetClinic = useStoreActions(actions => actions.clinic.onGetClinic);
  const onCreateClinicActTeam = useStoreActions(actions => actions.actTeam.onCreateClinicActTeam);

  const { isLoading } = useApiRequest(() => onGetClinic(clinicId), {
    initialLoading: true,
    dependencies: [clinicId],
  });

  const { mutate: onSubmit, isLoading: isCreating } = useApiMutation(
    ({ isShelterTeam = false, isCrisisTeam = false, ...values }: IInitialValues) =>
      onCreateClinicActTeam({
        clinicId,
        actTeam: { ...values, isShelterTeam, isCrisisTeam },
      }),
    {
      notifyMessage: 'Team Successfully added.',
      onSuccess: () =>
        navigate(VIEW_CLINIC.replace(':id', clinicId), { state: { selectedTab: 1 } }),
      throwOnError: false,
    },
  );

  if (isLoading || !clinic) return <Loader />;

  const breadcrumbItems = concat(breadcrumbItem, [
    {
      title: clinic?.name,
      to: VIEW_CLINIC.replace(':id', clinicId),
    },
    {
      title: 'Add New Team',
      to: location.pathname,
    },
  ]);

  const defaultValues = {
    clinicId,
    name: '',
  };

  const leavePageDescription = 'Are you sure you want to discard changes?';
  const cancelLinkTo = {
    pathname: VIEW_CLINIC.replace(':id', clinicId),
    state: { selectedTab: 1 },
  };

  return (
    <div>
      <h2>Add New Team</h2>
      <Breadcrumbs itemsForBreadcrumbs={breadcrumbItems} />
      <Form
        isEdit
        defaultValues={defaultValues}
        clinicSelectOptions={[
          { value: clinicId, label: clinic?.name, hasCrisisTeam: clinic?.hasCrisisTeam },
        ]}
        isProcessing={isCreating}
        cancelLinkTo={cancelLinkTo}
        leavePageDescription={leavePageDescription}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default CreateActTeam;
